import { Button, KeyboardShortcut, Separator } from "@my-saas/ui";
import { cn } from "@my-saas/utils";
import {
  BroadcastIcon,
  BuildingsIcon,
  CreditCardIcon,
  EnvelopeIcon,
  FadersHorizontalIcon,
  HouseIcon,
  MapPinIcon,
  PulseIcon,
  ShieldCheckIcon,
  TagIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router";
import useKeyboardShortcut from "use-keyboard-shortcut";

import { Copyright } from "@/client/components/copyright";
import { Icon } from "@/client/components/icon";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useUser } from "@/client/services/user";

type Props = {
  className?: string;
};

const ActiveIndicator = ({ className }: Props) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "size-1.5 animate-pulse rounded-full bg-info shadow-[0_0_12px] shadow-info",
      className,
    )}
  />
);

type SidebarItem = {
  path: string;
  name: string;
  shortcut?: string;
  icon: React.ReactNode;
};

type SidebarItemProps = SidebarItem & {
  onClick?: () => void;
};

const SidebarItem = ({
  path,
  name,
  shortcut,
  icon,
  onClick,
}: SidebarItemProps) => {
  const isActive = useLocation().pathname === path;

  return (
    <Button
      asChild
      size="lg"
      variant="ghost"
      className={cn(
        "h-auto justify-start px-4 py-3",
        isActive &&
          "pointer-events-none bg-secondary/50 text-secondary-foreground",
      )}
      onClick={onClick}
    >
      <Link to={path}>
        <div className="ms-0 me-3">{icon}</div>
        <span>{name}</span>
        {!isActive && (
          <KeyboardShortcut className="ms-auto">{shortcut}</KeyboardShortcut>
        )}
        {isActive && <ActiveIndicator className="ms-auto" />}
      </Link>
    </Button>
  );
};

type SidebarProps = {
  setOpen?: (open: boolean) => void;
};

export const Sidebar = ({ setOpen }: SidebarProps) => {
  const { user } = useUser();
  const navigate = useNavigate();

  useKeyboardShortcut(["shift", "d"], () => {
    void navigate("/dashboard");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "s"], () => {
    void navigate("/dashboard/settings");
    setOpen?.(false);
  });

  useKeyboardShortcut(["shift", "b"], () => {
    void navigate("/dashboard/billing");
    setOpen?.(false);
  });

  const isPlatformAdmin =
    (user as { platformRole?: string } | null)?.platformRole === "PLATFORM_ADMIN";

  // Top-level (always shown) — Hebrew strings inline. We avoid the t`` macro
  // because the Lingui catalog has no he-IL compilation for these keys, which
  // would otherwise render the short auto-generated message IDs (e.g. "IrqIMl").
  const sidebarItems: SidebarItem[] = [
    {
      path: "/dashboard",
      name: "ראשי",
      shortcut: "⇧D",
      icon: <HouseIcon />,
    },
    {
      path: "/dashboard/onboarding",
      name: "הארגונים שלי",
      icon: <BuildingsIcon />,
    },
    {
      path: "/dashboard/billing",
      name: "חיוב ותשלום",
      shortcut: "⇧B",
      icon: <CreditCardIcon />,
    },
    {
      path: "/dashboard/settings",
      name: "הגדרות חשבון",
      shortcut: "⇧S",
      icon: <FadersHorizontalIcon />,
    },
    ...(isPlatformAdmin
      ? ([
          {
            path: "/dashboard/platform/tenants",
            name: "ארגונים (מנהל מערכת)",
            icon: <ShieldCheckIcon />,
          },
          {
            path: "/dashboard/platform/raw-alerts",
            name: "יומן התרעות גולמי",
            icon: <BroadcastIcon />,
          },
          {
            path: "/dashboard/platform/engine",
            name: "סטטוס מנוע",
            icon: <PulseIcon />,
          },
        ] as SidebarItem[])
      : []),
  ];

  // Active-tenant sub-nav. When the URL is /dashboard/t/<slug>/*, show
  // tenant-specific links beneath the main sidebar.
  const location = useLocation();
  const tenantMatch = location.pathname.match(/^\/dashboard\/t\/([^/]+)/);
  const activeTenantSlug = tenantMatch?.[1];
  const tenantNavItems: SidebarItem[] = activeTenantSlug
    ? [
        {
          path: `/dashboard/t/${activeTenantSlug}`,
          name: "פיד חי",
          icon: <BroadcastIcon />,
        },
        {
          path: `/dashboard/t/${activeTenantSlug}/settings`,
          name: "הגדרות ארגון",
          icon: <FadersHorizontalIcon />,
        },
        {
          path: `/dashboard/t/${activeTenantSlug}/location-groups`,
          name: "אזורים",
          icon: <MapPinIcon />,
        },
        {
          path: `/dashboard/t/${activeTenantSlug}/alert-messages`,
          name: "הודעות התרעה",
          icon: <EnvelopeIcon />,
        },
        {
          path: `/dashboard/t/${activeTenantSlug}/card-mappings`,
          name: "שיוך התרעות פיקוד העורף",
          icon: <TagIcon />,
        },
        {
          path: `/dashboard/t/${activeTenantSlug}/members`,
          name: "חברי ארגון",
          icon: <UsersIcon />,
        },
      ]
    : [];

  return (
    <div className="flex h-full flex-col gap-y-4" dir="rtl">
      <div className="me-12 flex justify-center lg:me-0">
        <Button asChild size="icon" variant="ghost" className="size-10 p-0">
          <Link to="/" aria-label="cig.chat">
            <Icon size={28} className="mx-auto hidden lg:block" />
          </Link>
        </Button>
      </div>

      <Separator className="opacity-50" />

      <div className="grid gap-y-2">
        {sidebarItems.map((item) => (
          <SidebarItem
            {...item}
            key={item.path}
            onClick={() => setOpen?.(false)}
          />
        ))}
      </div>

      {tenantNavItems.length > 0 && (
        <>
          <Separator className="opacity-50" />
          <div className="px-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            ארגון פעיל
          </div>
          <div className="grid gap-y-1">
            {tenantNavItems.map((item) => (
              <SidebarItem
                {...item}
                key={item.path}
                onClick={() => setOpen?.(false)}
              />
            ))}
          </div>
        </>
      )}

      <div className="flex-1" />

      <Separator className="opacity-50" />

      <UserOptions>
        <Button size="lg" variant="ghost" className="w-full justify-start px-3">
          <UserAvatar size={24} className="me-3" />
          <span>{user?.name}</span>
        </Button>
      </UserOptions>

      <Copyright className="me-2" />
    </div>
  );
};
